<template>
    <div ref="outrightOddsComponent" class="outright-oddArea notranslate">
        <div v-if="teamName && isShowOption" class="outright-oddArea-oddType">
            {{ teamName }}
        </div>
        <div class="outright-oddArea-group" v-on:click="onClickOdds">
            <div
                class="odd"
                v-bind:class="[{
                    odd_rise: isPriceRise,
                    odd_drop: isPriceDrop,
                    odd_selected: isOddsSelected,
                    live: oddsData.isLive,
                }, ga]"
            >
                <div
                    class="odd-value"
                    v-bind:class="{ rtl: isReverseDisplayOddsEnabled }"
                >
                    {{ strReverse(formatMinDecimalPlace(oddsData.price, 2), isReverseDisplayOddsEnabled) }}
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { storeToRefs } from 'pinia';
    import type { PropType } from 'vue';
    import { computed, defineComponent, ref, toRefs } from 'vue';
    import type { IOutright, IOutrightTournament } from '@/interface/IOutright';
    import { DisplaySection } from '@/interface/enum';
    import {
        createOutrightTicket,
        createOutrightTicketKey,
    } from '@/libraries/betSlip/ticketHelper';
    import { useOutrightBetPage } from '@/libraries/composables/useBetPage';
    import { usePriceChange } from '@/libraries/composables/usePriceChange';
    import { useZone } from '@/libraries/composables/useZone';
    import { createEuroOutrightTicket } from '@/libraries/euroTicket';
    import { platform } from '@/libraries/helpers/platformHelper';
    import { getTranslated } from '@/libraries/helpers/translationHelper';
    import { formatMinDecimalPlace } from '@/libraries/utils/text';
    import { strReverse } from '@/libraries/utils/utils';
    import { useAccountStore } from '@/stores/accountStore';
    import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
    import { useOutrightBetSlipStore } from '@/stores/betSlip/outrightBetSlipStore';
    import { useSingleModeBetSlipStore } from '@/stores/betSlip/singleModeBetSlipStore';
    import { useFeatureToggleStore } from '@/stores/featureToggleStore';
    import { useRootStore } from '@/stores/rootStore';

    export default defineComponent({
        name: 'OutrightOdds',
        props: {
            oddsData: {
                type: Object as PropType<IOutright>,
                required: true,
            },
            outrightTournament: {
                type: Object as PropType<IOutrightTournament>,
                required: true,
            },
            displaySection: {
                type: Number as PropType<DisplaySection>,
                default: DisplaySection.Unknown,
            },
            isShowOption: {
                type: Boolean,
                default: true,
            },
        },
        setup(props) {
            const { oddsData, outrightTournament, displaySection } = toRefs(props);

            const { customerInfo } = storeToRefs(useAccountStore());
            const price = computed(() => ({
                value: oddsData.value.price,
                priceStyle: customerInfo.value.priceStyle,
            }));
            const { isPriceRise, isPriceDrop } = usePriceChange(price);

            const { isInSportsCupZone, isInEFootballZone, isInMultipleSportsZone, isInLeagueZone } = useZone();

            const { betPage } = useOutrightBetPage(displaySection.value);

            const teamName = computed(() => getTranslated(oddsData.value.teamName));

            const ga = computed(() => {
                if (displaySection.value === DisplaySection.TournamentCenter_Outright) {
                    return 'TC_Overview_Outright_Btn_Odds';
                }

                if (displaySection.value === DisplaySection.TournamentCenter_Groups) {
                    return 'TC_Standing_Group_WinnerMarket_Btn_Odds';
                }

                return 'JARVIS_MatchCard_Btn_Odds';
            });

            const {
                isReverseDisplayOddsEnabled,
                isSingleModeBetSlipEnabled,
            } = storeToRefs(useFeatureToggleStore());
            const { betSlip } = storeToRefs(useOutrightBetSlipStore());
            const {
                isTicketExist: isSingleModeTicketExist,
                addOrRemoveTicket: addOrRemoveSingleModeTicket,
            } = useSingleModeBetSlipStore();
            const { isAsi } = storeToRefs(useRootStore());
            const { isShowBetSlip, isShowOutrightBetSlip } = storeToRefs(useBetSlipStore());
            const singleModeTicketKey = computed(() => createOutrightTicketKey(oddsData.value.id));
            const isOddsSelected = computed(() => (
                isSingleModeBetSlipEnabled.value
                    ? isSingleModeTicketExist(singleModeTicketKey.value)
                    : isShowOutrightBetSlip.value && betSlip.value.id === oddsData.value.id
            ));

            const { removeTicket, addTicket, isTicketExist } = useOutrightBetSlipStore();
            const { setOddsPosition } = useRootStore();

            const outrightOddsComponent = ref();
            async function onClickOdds() {
                if (!isAsi.value) {
                    platform.showLogin('Sports');
                    return;
                }

                if (isSingleModeBetSlipEnabled.value) {
                    const source = createEuroOutrightTicket(
                        oddsData.value,
                        betPage.value,
                        outrightTournament.value,
                    );
                    const ticket = createOutrightTicket(source);
                    await addOrRemoveSingleModeTicket(ticket);
                    return;
                }

                if (isTicketExist(oddsData.value.id)) {
                    await removeTicket();
                    setOddsPosition(0);
                    return;
                }

                const outrightTicket = createEuroOutrightTicket(oddsData.value, betPage.value, outrightTournament.value);
                addTicket(outrightTicket);
                const isTicketElExisted = isShowBetSlip.value;

                if (outrightOddsComponent.value) {
                    setTimeout(() => {
                        const yPosition = outrightOddsComponent.value.getBoundingClientRect().y;
                        if (yPosition > 0) {
                            setOddsPosition(yPosition);
                        }
                    }, isTicketElExisted ? 50 : 300);
                }
            }

            return {
                isPriceRise,
                isPriceDrop,
                isInSportsCupZone,
                isInEFootballZone,
                isInMultipleSportsZone,
                isInLeagueZone,
                teamName,
                isReverseDisplayOddsEnabled,
                isShowBetSlip,
                isOddsSelected,
                formatMinDecimalPlace,
                strReverse,
                onClickOdds,
                ga,
            };
        },
    });
</script>

<style lang="scss" scoped>
@import "@/scss/components/sports/marketCard/outrightOdds";
@import "@/scss/components/odd";
</style>
